import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Grid,
} from "@mui/material";
import AccessAlarmIcon from "@mui/icons-material/AccessAlarm";
import SideBar from "../Component/SideBar";
import { useParams, useNavigate } from "react-router-dom";
import { fetchExamById, submitTestAnswers } from "../Api/examApi";

export default function QuizPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [timer, setTimer] = useState(600);
  const [currentQIndex, setCurrentQIndex] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [showQuestionPaper, setShowQuestionPaper] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false); // ✅ prevent retake
  const timerRef = useRef(null);

  // === Timer logic ===
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          alert("Time is up! Your test will be submitted automatically.");
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, []);

  const formatTime = (seconds) => {
    const mins = String(Math.floor(seconds / 60)).padStart(2, "0");
    const secs = String(seconds % 60).padStart(2, "0");
    return `${mins}:${secs}`;
  };

  // === Load exam data ===
  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await fetchExamById(id);

        // ✅ If the test is already submitted
        if (data.hasSubmitted) {
          alert("You have already completed this test.");
          navigate("/ListQuestionBank", { replace: true });
          return;
        }

        setExam(data);
        setQuestions(data.questionInformation?.questions || []);
        setTimer(data.generalInformation?.durationInMinutes * 60 || 600);
      } catch (err) {
        console.error("Failed to load questions", err);
        alert("Failed to load test.");
        navigate("/ListQuestionBank", { replace: true });
      }
    };
    loadData();
  }, [id, navigate]);

  // === Prevent back navigation if submitted ===
  useEffect(() => {
    if (hasSubmitted) {
      navigate("/ListQuestionBank", { replace: true });
    }
  }, [hasSubmitted, navigate]);

  useEffect(() => {
    const handlePopState = () => {
      if (hasSubmitted) {
        navigate("/ListQuestionBank", { replace: true });
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [hasSubmitted, navigate]);

  // === Handle answers ===
  const handleAnswerChange = (qIndex, selectedAnswer) => {
    if (hasSubmitted) return; // prevent answer changes after submission
    const question = questions[qIndex];
    if (question.IsMultipleCorrect) {
      setAnswers((prev) => {
        const currentSelections = prev[qIndex] || [];
        const newSelections = currentSelections.includes(selectedAnswer)
          ? currentSelections.filter((id) => id !== selectedAnswer)
          : [...currentSelections, selectedAnswer];
        return { ...prev, [qIndex]: newSelections };
      });
    } else {
      setAnswers((prev) => ({
        ...prev,
        [qIndex]: selectedAnswer,
      }));
    }
  };

  const handleClearAnswer = (qIndex) => {
    if (hasSubmitted) return; // prevent clearing after submission
    setAnswers((prev) => {
      const newAnswers = { ...prev };
      delete newAnswers[qIndex];
      return newAnswers;
    });
  };

  // === Submit test ===
  const handleSubmit = async () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    const formattedAnswers = questions.map((question, index) => {
      const selected = answers[index];
      return {
        questionId: question.id,
        selectedOptions: Array.isArray(selected)
          ? selected
          : selected
          ? [selected]
          : [],
      };
    });

    const payload = { id: id, answers: formattedAnswers };

    try {
      const response = await submitTestAnswers(payload);
      if (response.questionInformation?.questions) {
        setQuestions(response.questionInformation.questions);
      }
      setResultData(response.result);
      setShowResult(true);
      setHasSubmitted(true); // prevent retake
    } catch (error) {
      alert("Failed to submit the test.");
    }
  };

  // === Result page ===
  if (showResult && resultData && exam) {
    return (
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}>
        <SideBar />
        <Box
          sx={{
            flexGrow: 1,
            px: { xs: 2, sm: 3, md: 5 },
            pt: { xs: 7, sm: 8 },
          }}
        >
          <Paper
            elevation={3}
            sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, bgcolor: "#e8f5e9" }}
          >
            <Typography variant="h4" color="success.main" gutterBottom>
              🎉 Test Submitted Successfully!
            </Typography>
            <Typography variant="h6" mb={2}>
              ✅ Result Summary
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography>
                  <strong>Test Name:</strong> {exam.generalInformation.name}
                </Typography>
                <Typography>
                  <strong>Subject:</strong> {exam.generalInformation.subject}
                </Typography>
                <Typography>
                  <strong>Topic:</strong> {exam.generalInformation.topic}
                </Typography>
                <Typography>
                  <strong>Language:</strong> {exam.generalInformation.language}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography>
                  <strong>Duration:</strong>{" "}
                  {exam.generalInformation.durationInMinutes} min
                </Typography>
                <Typography>
                  <strong>Total Questions:</strong> {resultData.total}
                </Typography>
                <Typography>
                  <strong>Attempted:</strong> {resultData.attempted}
                </Typography>
                <Typography>
                  <strong>Correct:</strong> {resultData.correct}
                </Typography>
                <Typography>
                  <strong>Incorrect:</strong> {resultData.incorrect}
                </Typography>
                <Typography>
                  <strong>Marks Scored:</strong> {resultData.marks}
                </Typography>
                
              </Grid>
            </Grid>

            <Box mt={4}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setShowQuestionPaper(true)}
              >
                View Result
              </Button>
            </Box>

            {showQuestionPaper && (
              <Box mt={4}>
                <Typography variant="h6" gutterBottom>
                  📘 Full Question Paper with Correct and Selected Answers
                </Typography>
                {questions.map((question, index) => {
                  const selectedOptions = answers[index] || [];
                  const isQuestionCorrect = question.correct === true;

                  return (
                    <Paper
                      key={question.id || index}
                      sx={{
                        p: 2,
                        mb: 2,
                        border: isQuestionCorrect
                          ? "2px solid #4caf50"
                          : "2px solid #f44336",
                        backgroundColor: isQuestionCorrect
                          ? "#f1f8e9"
                          : "#ffebee",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: isQuestionCorrect ? "#2e7d32" : "#d32f2f",
                          fontWeight: 600,
                        }}
                      >
                        {index + 1}. {question.question}
                        <span style={{ marginLeft: 8 }}>
                          {isQuestionCorrect ? "✅ Correct" : "❌ Wrong"}
                        </span>
                      </Typography>

                      <Box ml={2}>
                        {question.options.map((opt) => {
                          const isSelected = question.IsMultipleCorrect
                            ? (selectedOptions || []).includes(opt.id)
                            : selectedOptions === opt.id;
                          const isOptionCorrect = opt.isCorrect === true;

                          let color, fontWeight, backgroundColor, border;
                          if (isOptionCorrect && isSelected) {
                            color = "#2e7d32";
                            fontWeight = 600;
                            backgroundColor = "#e8f5e9";
                            border = "2px solid #4caf50";
                          } else if (isOptionCorrect) {
                            color = "#2e7d32";
                            fontWeight = 600;
                            backgroundColor = "#f1f8e9";
                            border = "2px solid #4caf50";
                          } else if (isSelected) {
                            color = "#d32f2f";
                            fontWeight = 600;
                            backgroundColor = "#ffebee";
                            border = "2px solid #f44336";
                          } else {
                            color = "#757575";
                            fontWeight = "normal";
                            backgroundColor = "#fafafa";
                            border = "1px solid #e0e0e0";
                          }

                          return (
                            <Box
                              key={opt.id}
                              sx={{
                                color,
                                fontWeight,
                                mb: 1,
                                p: "8px 12px",
                                borderRadius: "6px",
                                backgroundColor,
                                border,
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              {question.IsMultipleCorrect ? (
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  disabled
                                  style={{ marginRight: 8 }}
                                />
                              ) : (
                                <input
                                  type="radio"
                                  checked={isSelected}
                                  disabled
                                  style={{ marginRight: 8 }}
                                />
                              )}
                              <Typography component="span">
                                <strong>{opt.id}.</strong> {opt.answer}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    );
  }

  // === Main Quiz View ===
  return (
    <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}>
      <SideBar />
      <Box
        sx={{ flexGrow: 1, px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 7, sm: 8 } }}
      >
        <Grid
          container
          spacing={2}
          sx={{ pt: { xs: 1, sm: 2, md: 3, lg: 4, xl: 5 } }}
        >
          <Grid item xs={12} md={9}>
            <Typography variant="h6" gutterBottom>
              Test Name : {exam?.generalInformation?.name || "N/A"}{" "}
              &nbsp;&nbsp;&nbsp; Subject :{" "}
              {exam?.generalInformation?.subject || "N/A"}
            </Typography>

            {hasSubmitted ? (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" color="error">
                  You have already submitted this test. You cannot go back or
                  change answers.
                </Typography>
              </Paper>
            ) : questions.length === 0 ? (
              <Typography>No questions available.</Typography>
            ) : (
              <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
                <Typography variant="subtitle1" mb={1}>
                  {currentQIndex + 1}. {questions[currentQIndex].question}
                </Typography>

                <FormControl component="fieldset">
                  {questions[currentQIndex].IsMultipleCorrect ? (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {questions[currentQIndex].options.map((option) => (
                        <FormControlLabel
                          key={option.id}
                          control={
                            <input
                              type="checkbox"
                              checked={(answers[currentQIndex] || []).includes(
                                option.id
                              )}
                              onChange={() =>
                                handleAnswerChange(currentQIndex, option.id)
                              }
                              disabled={hasSubmitted}
                            />
                          }
                          label={`${option.id}. ${option.answer}`}
                        />
                      ))}
                    </Box>
                  ) : (
                    <RadioGroup
                      value={answers[currentQIndex] || ""}
                      onChange={(e) =>
                        handleAnswerChange(currentQIndex, e.target.value)
                      }
                    >
                      {questions[currentQIndex].options.map((option) => (
                        <FormControlLabel
                          key={option.id}
                          value={option.id}
                          control={<Radio disabled={hasSubmitted} />}
                          label={`${option.id}. ${option.answer}`}
                        />
                      ))}
                    </RadioGroup>
                  )}
                </FormControl>

                <Box
                  mt={2}
                  display="flex"
                  flexDirection={{ xs: "column", sm: "row" }}
                  gap={1}
                >
                  <Button
                    variant="outlined"
                    disabled={currentQIndex === 0 || hasSubmitted}
                    onClick={() => setCurrentQIndex(currentQIndex - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outlined"
                    color="warning"
                    disabled={hasSubmitted}
                    onClick={() => handleClearAnswer(currentQIndex)}
                  >
                    Clear
                  </Button>
                  {currentQIndex === questions.length - 1 ? (
                    <Button
                      variant="contained"
                      color="error"
                      disabled={hasSubmitted}
                      onClick={() =>
                        window.confirm(
                          "Are you sure you want to submit the test?"
                        ) && handleSubmit()
                      }
                    >
                      Submit Test
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      disabled={hasSubmitted}
                      onClick={() => setCurrentQIndex(currentQIndex + 1)}
                    >
                      Save & Next
                    </Button>
                  )}
                </Box>
              </Paper>
            )}
          </Grid>

          <Grid item xs={12} md={3}>
            <Box
              sx={{
                border: "1px solid #ccc",
                borderRadius: 2,
                p: 2,
                bgcolor: "#f8f8f8",
                textAlign: "center",
              }}
            >
              <Box
                sx={{
                  bgcolor: "#03A9F4",
                  color: "white",
                  borderRadius: 1,
                  p: 1,
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  fontWeight: 600,
                }}
              >
                <AccessAlarmIcon /> Time Left - {formatTime(timer)}
              </Box>

              <Typography variant="h6" gutterBottom>
                Question Navigation
              </Typography>
              <Box
                display="flex"
                flexWrap="wrap"
                justifyContent="center"
                gap={1}
                mt={1}
              >
                {questions.map((_, index) => {
                  let bgColor = "#9E9E9E";
                  let hoverColor = "#757575";
                  if (index === currentQIndex) {
                    bgColor = "#2196F3";
                    hoverColor = "#1976D2";
                  } else if (answers[index] !== undefined) {
                    bgColor = "#4CAF50";
                    hoverColor = "#388E3C";
                  }

                  return (
                    <Button
                      key={index}
                      variant="contained"
                      onClick={() => !hasSubmitted && setCurrentQIndex(index)}
                      sx={{
                        minWidth: 40,
                        minHeight: 40,
                        backgroundColor: bgColor,
                        color: "#fff",
                        "&:hover": { backgroundColor: hoverColor },
                      }}
                    >
                      {index + 1}
                    </Button>
                  );
                })}
              </Box>

              <Button
                variant="contained"
                fullWidth
                sx={{ backgroundColor: "#03A9F4", mt: 2 }}
                disabled={hasSubmitted}
                onClick={() =>
                  window.confirm("Are you sure you want to submit the test?") &&
                  handleSubmit()
                }
              >
                Submit Test
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
